import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Modal, Field, SaButton, sa, errMsg } from './ui';

export const CompanyUsersModal = ({ company, onClose, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', password: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`/superadmin/companies/${company.id}/users`);
      setUsers(data);
    } catch (e) {
      toast.error('Could not load users');
    }
  };
  useEffect(() => { load(); }, [company.id]); // eslint-disable-line

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error('Email required');
    setBusy(true);
    try {
      await axios.post(`/superadmin/companies/${company.id}/admins`, { email: form.email, name: form.name || undefined, password: form.password || undefined });
      toast.success('Admin assigned');
      setForm({ email: '', name: '', password: '' });
      await load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err, 'Failed'));
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (u, role) => {
    try {
      await axios.patch(`/superadmin/companies/${company.id}/users/${u.id}`, { role });
      toast.success(`${u.email} is now ${role}`);
      await load(); onChanged();
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 chars):`);
    if (!pw) return;
    try {
      await axios.post(`/superadmin/companies/${company.id}/users/${u.id}/reset-password`, { new_password: pw });
      toast.success('Password reset');
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/superadmin/companies/${company.id}/users/${u.id}`);
      toast.success('User deleted');
      await load(); onChanged();
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
  };

  return (
    <Modal title={`${company.name} · Users`} onClose={onClose} width={720} testId="company-users-modal">
      <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #27272A' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#0A0A0A' }}>
              {['User', 'Role', ''].map((h) => <th key={h} style={{ ...sa.overline, textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #27272A' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={3} style={{ padding: 16, color: '#A1A1AA' }}>No users yet — assign the first admin below.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} data-testid={`company-user-row-${u.id}`} style={{ borderBottom: '1px solid #1f1f22' }}>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600 }}>{u.name}</div>
                  <div style={{ color: '#A1A1AA', fontSize: 12 }}>{u.email}</div>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ color: u.role === 'admin' ? '#ff6b6b' : '#A1A1AA', fontWeight: 700, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.1em' }}>{u.role}</span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {u.role === 'admin'
                    ? <SaButton variant="ghost" data-testid={`demote-user-${u.id}`} style={{ padding: '5px 10px' }} onClick={() => setRole(u, 'employee')}>Demote</SaButton>
                    : <SaButton variant="ghost" data-testid={`promote-user-${u.id}`} style={{ padding: '5px 10px' }} onClick={() => setRole(u, 'admin')}>Make admin</SaButton>}
                  <SaButton variant="ghost" style={{ padding: '5px 10px', marginLeft: 6 }} onClick={() => resetPw(u)}>Reset PW</SaButton>
                  <SaButton variant="danger" data-testid={`delete-user-${u.id}`} style={{ padding: '5px 10px', marginLeft: 6 }} onClick={() => remove(u)}>Delete</SaButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={addAdmin} style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #27272A' }}>
        <p style={{ ...sa.overline, marginBottom: 12 }}>Assign company admin</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <Field label="Email"><input data-testid="assign-admin-email" style={sa.input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="existing user or new" /></Field>
          <Field label="Name (new users)"><input data-testid="assign-admin-name" style={sa.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Password (new users)"><input data-testid="assign-admin-password" style={sa.input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <SaButton data-testid="assign-admin-submit" type="submit" disabled={busy} style={{ marginBottom: 14 }}>Assign</SaButton>
        </div>
      </form>
    </Modal>
  );
};
